import axios from 'axios';
import { lenientParse, isNumberCellEmpty, formatChartData, createDateAsUTC } from '../../services/items';

export const getRenewables = async (itemTypeId) => {
    const response = await axios.get(`/items/${itemTypeId}/items`);
    return response.data;
};

export const getSubRenewables = async (itemId) => {
    const response = await axios.get(`/renewables/${itemId}/sub-renewables`);
    return response.data;
};

export const editRenewable = (payload) => {
    return axios.put('/renewables/modify-renewable-sub-renewables', payload);
};

export const copyRenewable = async (renewableId) => {
    const response = await axios.post(`/items/${renewableId}/copy`);
    return response.data;
};

export const deleteRenewable = (renewableId) => axios.delete(`/items/${renewableId}`);

export const findRenewable = (items, renewableId) => {
    return items.find((item) => item.id === Number(renewableId));
};

export const addRenewableToPortfolios = (renewableId, payload) =>
    axios.post(`/items/${renewableId}/portfolios`, payload);

export const createRenewableDefinition = async (payload) => {
    const response = await axios.post(`/renewables/sub-renewable`, payload);
    return response.data;
};

export const deleteSubRenewable = (subRenewableId) => axios.delete(`/renewables/${subRenewableId}`);

export const getRenewableDataChart = async (renewableId, payload) => {
    const response = await axios.post(`/renewables/${renewableId}/renewable-data-sampling`, payload);

    return formatChartData(response.data);
};

export const getRenewableForecastChart = async (renewableId, payload) => {
    const response = await axios.post(`/renewables/${renewableId}/renewable-forecast-sampling`, payload);

    return formatChartData(response.data);
};

export const getRenewableData = async (renewableId, payload) => {
    const response = await axios.post(`/renewables/${renewableId}/renewable-data`, payload);

    return response.data;
};

export const getRenewableForecast = async (renewableId, payload) => {
    const response = await axios.post(`/renewables/${renewableId}/renewable-forecast`, payload);

    return response.data;
};

export const getRenewableCosts = async (renewableId, payload) => {
    const response = await axios.get(`/renewables/${renewableId}/renewable-cost`, { params: payload });

    return response.data;
};

export const getRenewableAncillaries = async (renewableId, payload) => {
    const response = await axios.get(`/renewables/${renewableId}/renewable-ancillary`, { params: payload });

    return response.data;
};

export const getRenewableModels = async (renewableId, payload) => {
    const response = await axios.get(`/renewables/${renewableId}/renewable-model`, { params: payload });

    return response.data;
};

export const updateRenewableData = (subRenewableId, payload) =>
    axios.put(`/renewables/${subRenewableId}/renewable-data`, payload);

export const updateRenewableForecast = (subRenewableId, payload) =>
    axios.put(`/renewables/${subRenewableId}/renewable-forecasts`, payload);

export const updateRenewableCosts = (subRenewableId, payload) =>
    axios.put(`/renewables/${subRenewableId}/renewable-costs`, payload);

export const updateRenewableAncillaries = (subRenewableId, payload) =>
    axios.put(`/renewables/${subRenewableId}/renewable-ancillaries`, payload);

export const updateRenewableModels = (subRenewableId, payload) =>
    axios.put(`/renewables/${subRenewableId}/renewable-models`, payload);

export const formatParsedNewRenewableData = (data) => {
    return data.map(({ startDate, endDate, updateDateTime, windOrSolar }) => {
        return {
            startDate: startDate ? createDateAsUTC(lenientParse(startDate)) : null,
            endDate: endDate ? createDateAsUTC(lenientParse(endDate)) : null,
            updateDateTime: updateDateTime ? createDateAsUTC(lenientParse(updateDateTime)) : null,
            windOrSolar: isNumberCellEmpty(windOrSolar),
        };
    });
};

export const formatParsedNewRenewableForecast = (data, forecastTypes) => {
    return data.map((row) => {
        return {
            startDate: row.startDate ? createDateAsUTC(lenientParse(row.startDate)) : null,
            endDate: row.endDate ? createDateAsUTC(lenientParse(row.endDate)) : null,
            updateDateTime: row.updateDateTime ? createDateAsUTC(lenientParse(row.updateDateTime)) : null,
            forecastTypeId: forecastTypes.find((type) => type.description === row.forecastTypeId).id,
            expectedEnergy: isNumberCellEmpty(row.expectedEnergy),
            expectedPeakGeneration: isNumberCellEmpty(row.expectedPeakGeneration),
            capacity: isNumberCellEmpty(row.capacity),
        };
    });
};

export const formatParsedNewRenewableCosts = (data) => {
    return data.map((row) => {
        return {
            startDate: row.startDate ? createDateAsUTC(lenientParse(row.startDate)) : null,
            endDate: row.endDate ? createDateAsUTC(lenientParse(row.endDate)) : null,
            updateDateTime: row.updateDateTime ? createDateAsUTC(lenientParse(row.updateDateTime)) : null,
            fixedCost: isNumberCellEmpty(row.fixedCost),
            vom: isNumberCellEmpty(row.vom),
            curtailPrice: isNumberCellEmpty(row.curtailPrice),
            curtailPercentage: isNumberCellEmpty(row.curtailPercentage),
        };
    });
};

export const formatParsedNewRenewableAncillaries = (data) => {
    return data.map((row) => {
        return {
            startDate: row.startDate ? createDateAsUTC(lenientParse(row.startDate)) : null,
            endDate: row.endDate ? createDateAsUTC(lenientParse(row.endDate)) : null,
            canContributeContingent: Number(row.canContributeContingent) === 1 ? true : false, // required field
        };
    });
};

export const formatParsedNewRenewableModels = (data) => {
    return data.map((row) => {
        return {
            ...row,
            useFlag: Number(row.useFlag) === 1 ? true : false, // required field
            runOrder: isNumberCellEmpty(row.runOrder),
        };
    });
};

export const createRenewableData = (subRenewableId, payload) =>
    axios.post(`/renewables/${subRenewableId}/create-renewable-data`, payload);

export const createRenewableForecasts = (subRenewableId, payload) =>
    axios.post(`/renewables/${subRenewableId}/create-renewable-forecast`, payload);

export const createRenewableCosts = (subRenewableId, payload) =>
    axios.post(`/renewables/${subRenewableId}/create-renewable-costs`, payload);

export const createRenewableAncillaries = (subRenewableId, payload) =>
    axios.post(`/renewables/${subRenewableId}/create-renewable-ancillaries`, payload);

export const createRenewableModels = (subRenewableId, payload) =>
    axios.post(`/renewables/${subRenewableId}/create-renewable-models`, payload);

export const deleteRenewableData = (renewableId, payload) =>
    axios.delete(`/renewables/${renewableId}/renewable-data`, { data: payload });

export const deleteRenewableForecasts = (renewableId, payload) =>
    axios.delete(`/renewables/${renewableId}/renewable-forecasts`, { data: payload });

export const deleteRenewableCosts = (renewableId, payload) =>
    axios.delete(`/renewables/${renewableId}/renewable-costs`, { data: payload });

export const deleteRenewableAncillaries = (renewableId, payload) =>
    axios.delete(`/renewables/${renewableId}/renewable-ancillaries`, { data: payload });

export const deleteRenewableModels = (renewableId, payload) =>
    axios.delete(`/renewables/${renewableId}/renewable-models`, { data: payload });

export const deleteAllRenewableData = (renewableId) =>
    axios.delete(`/renewables/${renewableId}/delete-all-renewable-data`);

export const deleteAllRenewableForecasts = (renewableId) =>
    axios.delete(`/renewables/${renewableId}/delete-all-renewable-forecasts`);

export const deleteAllRenewableCosts = (renewableId) =>
    axios.delete(`/renewables/${renewableId}/delete-all-renewable-costs`);

export const deleteAllRenewableAncillaries = (renewableId) =>
    axios.delete(`/renewables/${renewableId}/delete-all-renewable-ancillaries`);

export const deleteAllRenewableModels = (renewableId) =>
    axios.delete(`/renewables/${renewableId}/delete-all-renewable-models`);
